<template>
  <LISADatePicker
    v-if="isLISA3"
    v-bind="{
      ...$attrs,
    }"
    :disabled="disabled"
    :range="range"
    :editable="false"
    :with-time="showHoursInput"
    :granularity="toLISADatePickerGranularity(type)"
    :locale="locale"
    :inline="inline"
    :open-range="partialRange"
    :auto-apply="autoApply"
    :time-zone="timezone"
    :min-date="dateToTemporalPlainDate(minDate)"
    :max-date="dateToTemporalPlainDate(maxDate)"
    :date-disabled-function="toLISADatePickerDateDisabledFunction(dateDisabledFn)"
    :model-value="lisa3ModelValue"
    weekend-color="var(--lisa-secondary)"
    @update:model-value="handleLISA3ModelValue"
  >
    <template v-if="'prepend' in $slots" #prepend>
      <slot name="prepend" />
    </template>
    <template v-if="'append' in $slots" #append>
      <slot name="append" />
    </template>
  </LISADatePicker>
  <div v-else class="w-100" style="display: inline-block" :data-testid="dataTestid">
    <div :class="{ 'date-picker-input-layout': showHoursInput }">
      <InputGroup>
        <InputGroupAddon v-if="!hideCalendarIcon" class="p-0">
          <slot name="prepend">
            <i class="fas" :class="calendarIcon" />
          </slot>
        </InputGroupAddon>
        <VueDatePicker
          ref="datePicker"
          :model-value="displayedModelValue"
          :formats="{ input: formatter }"
          :locale="localeObject"
          :placeholder="placeholder ?? ''"
          six-weeks
          :week-numbers="{ type: 'iso', label: t('components.HeadNavbar.week').slice(0, 3) }"
          :input-attrs="{
            hideInputIcon: true,
            clearable: clearable ?? false,
          }"
          :auto-apply="autoApply"
          :config="{
            keepActionRow: true,
          }"
          :inline="inline"
          :ui="{
            input: `p-inputtext p-component w-100 CustomDatePickerClassInput ${
              stateAsString === 'true' ? 'p-valid' : stateAsString == null ? '' : 'p-invalid'
            }`,
            menu: 'CustomDatePickerClassMenu',
          }"
          :teleport="!inline"
          v-bind="pickerMode"
          :time-config="timeConfig"
          :text-input="false"
          :highlight="{ weekdays: [0, 6] }"
          :dark="false"
          :disabled="disabled"
          :month-picker="monthPicker"
          :week-picker="weekPicker"
          :year-picker="yearPicker"
          :min-date="minDate"
          :max-date="maxDate"
          :disabled-dates="dateDisabledFn"
          :hide-navigation="[]"
          ignore-time-validation
          :action-row="{ showSelect: true, showCancel: true, showNow: false, showPreview: true }"
          @internal-model-change="handleInternalModelChange"
          @update:model-value="handleCommitModelValue"
          @closed="syncDisplayedModelValue"
        >
          <template #clear-icon="{ clear }">
            <i class="fas fa-times mr-3" @click.stop="clear" />
          </template>
          <template #action-row="actions">
            <div class="w-100">
              <div>
                <slot name="bottom-info" />
              </div>
              <div v-if="showDayBoundaryButtons" class="p-buttonset w-100">
                <LISAButton
                  class="p-button-text w-50"
                  data-testid="startOfDay"
                  @click.stop="setDayBoundary(actions.modelValue, 'start')"
                >
                  {{ t("components.shared.DatePickerComponent.startOfDay") }}
                </LISAButton>
                <LISAButton
                  class="p-button-text w-50"
                  data-testid="endOfDay"
                  @click.stop="setDayBoundary(actions.modelValue, 'end')"
                >
                  {{ t("components.shared.DatePickerComponent.endOfDay") }}
                </LISAButton>
              </div>
              <div class="p-buttonset w-100">
                <slot name="resetButton" :actions="actions">
                  <LISAButton
                    class="p-button-text w-50"
                    data-testid="now"
                    @click.stop="resetDateToToday()"
                  >
                    <div class="text-center">
                      {{ t("components.bcalendar.today") }}
                    </div>
                  </LISAButton>
                </slot>
                <LISAButton
                  class="w-50 DatePickerSaveButton"
                  data-testid="selectDate"
                  :disabled="actions.disabled || isSaveDisabled(actions.modelValue)"
                  @click="actions.selectDate"
                >
                  <i class="fas fa-save mr-1" />
                  {{ selectButtonLabel ?? t("global.save") }}
                </LISAButton>
              </div>
            </div>
          </template>
        </VueDatePicker>
        <slot name="append" />
      </InputGroup>
      <span v-if="showHoursInput" class="date-picker-hours-separator">-</span>
      <InputGroup v-if="showHoursInput" class="date-picker-hours-input">
        <LISAInputNumber
          :model-value="durationHours"
          :max-fraction-digits="2"
          :disabled="disabled"
          readonly
          :input-class="'text-right'"
        />
        <InputGroupAddon>
          {{ t("global.hoursShort") }}
        </InputGroupAddon>
      </InputGroup>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends boolean">
/**
 * For more information visit https://vue3datepicker.com/
 */
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { VueDatePicker, type InternalModelValue, type RootProps } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import LISAButton from "@LISAPrime/LISAButton.vue";
import { locale } from "@/language/translation";
import { TimeZoneInjectionKey } from "@/functions/timezoneUtil";
import { DatePickerType } from "./DatePickerComponentHelper";
import InputGroupAddon from "primevue/inputgroupaddon";
import InputGroup from "primevue/inputgroup";
import LISAInputNumber from "@/components/primevueWrapper/LISAInputNumber.vue";
import injectAsserted from "@/functions/assert/injectAsserted";
import { computedAsync } from "@vueuse/core";
import type { languageNames } from "@/language";
import type { Locale } from "date-fns";
import {
  LISADatePicker,
  type LISADatePickerDate,
  type LISADatePickerDateDisabledFunction,
  type LISADatePickerGranularity,
  type LISADatePickerRangeValue,
  type LISADatePickerValue,
  type LISATemporalPlainDate,
} from "@fi-solutions/lisa-ui";
import { isLISA3 } from "@/composables/useLISA3";

type DateOrUndefined = DateTime | undefined;
type ModelValue<T extends boolean> = T extends true ? DateOrUndefined[] : DateOrUndefined;
const props = defineProps<{
  range?: T;
  modelValue?: ModelValue<T>;
  type?: DatePickerType;
  disabled?: boolean;
  clearable?: boolean;
  stateAsString?: string;
  dateDisabledFn?: (date: Date) => boolean;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  keepActionRow?: boolean;
  dataTestid?: string;
  inline?: boolean;
  hideCalendarIcon?: boolean;
  partialRange?: boolean;
  showHours?: boolean;
  selectButtonLabel?: string;
}>();

const autoApply = computed(
  () =>
    !props.inline &&
    !props.range &&
    [DatePickerType.DAY, DatePickerType.WEEK, DatePickerType.MONTH, DatePickerType.YEAR].includes(
      props.type ?? DatePickerType.DAY,
    ),
);

const emit = defineEmits<{
  (event: "update:modelValue", value: any): any;
}>();

const { t } = useTranslation();
const timezone = injectAsserted(TimeZoneInjectionKey);
const datePicker = useTemplateRef("datePicker");

const pickerMode = computed(() => {
  const isRange = props.range === true || Array.isArray(props.modelValue);

  let flow: RootProps["flow"] = { steps: ["calendar", "time"] };

  if ([DatePickerType.MONTH, DatePickerType.YEAR].includes(props.type ?? DatePickerType.MINUTE)) {
    flow = undefined;
  }
  if ([DatePickerType.DAY, DatePickerType.WEEK].includes(props.type ?? DatePickerType.MINUTE)) {
    flow = { steps: ["calendar"] };
  }
  return {
    /**
     * if range is supplied or requested
     */
    range: isRange ? { partialRange: props.partialRange } : undefined,
    "multi-calendars": isRange,

    flow,
    "week-picker": props.type === DatePickerType.WEEK,
    "year-picker": props.type === DatePickerType.YEAR,
    "month-picker": props.type === DatePickerType.MONTH,
  };
});

const calendarIcon = computed(() => {
  switch (props.type) {
    case DatePickerType.DAY:
      return "fa-calendar-day";
    case DatePickerType.WEEK:
      return "fa-calendar-week";
    default:
      return "fa-calendar";
  }
});

const enableSeconds = computed(() => {
  return props.type === DatePickerType.SECOND;
});

const enableMinutes = computed(() => {
  return [DatePickerType.MINUTE, DatePickerType.SECOND].includes(
    props.type ?? DatePickerType.MINUTE,
  );
});
const timeConfig = computed<NonNullable<RootProps["timeConfig"]>>(() => ({
  enableTimePicker: [DatePickerType.HOUR, DatePickerType.MINUTE, DatePickerType.SECOND].includes(
    props.type ?? DatePickerType.MINUTE,
  ),
  enableSeconds: enableSeconds.value,
  enableMinutes: enableMinutes.value,
  is24: t("components.shared.DatePickerComponent.hourOnly").includes("HH"),
  timePickerInline: true,
}));

const showDayBoundaryButtons = computed(() =>
  [DatePickerType.HOUR, DatePickerType.MINUTE, DatePickerType.SECOND].includes(
    props.type ?? DatePickerType.MINUTE,
  ),
);
const monthPicker = computed(() => {
  return props.type === DatePickerType.MONTH;
});
const weekPicker = computed(() => {
  return props.type === DatePickerType.WEEK;
});
const yearPicker = computed(() => {
  return props.type === DatePickerType.YEAR;
});

const showHoursInput = computed(() => props.range === true && props.showHours === true);

const startRangeDate = computed(() => {
  if (!Array.isArray(props.modelValue)) {
    return undefined;
  }
  return props.modelValue[0];
});

const endRangeDate = computed(() => {
  if (!Array.isArray(props.modelValue)) {
    return undefined;
  }
  return props.modelValue[1];
});

const durationHours = computed(() => {
  if (startRangeDate.value == null || endRangeDate.value == null) {
    return 0;
  }
  return Math.max(
    0,
    Number(endRangeDate.value.diff(startRangeDate.value, "hours").hours.toFixed(2)),
  );
});

const formatString = computed(() => {
  switch (props.type) {
    case DatePickerType.SECOND:
      return "datetimeLong";
    case DatePickerType.MINUTE:
      return "datetime";
    case DatePickerType.HOUR:
      return "datetimeWithOutMinute";
    case DatePickerType.DAY:
      return "date";
    case DatePickerType.MONTH:
      return "yearWithMonth2Digits";
    case DatePickerType.YEAR:
      return "years";
    case DatePickerType.WEEK:
      return "week";
    default:
      return "datetimeLong";
  }
});

type MonthPickerValue = {
  month: number;
  year: number;
};

function isMonthPickerValue(value: unknown): value is MonthPickerValue {
  return (
    value != null &&
    typeof value === "object" &&
    "month" in value &&
    "year" in value &&
    typeof value.month === "number" &&
    typeof value.year === "number"
  );
}

function toFormatterDate(value: unknown): Date {
  if (value instanceof Date) {
    return isNaN(value.valueOf()) ? new Date() : value;
  }

  if (DateTime.isDateTime(value)) {
    return value.toJSDate();
  }

  if (isMonthPickerValue(value)) {
    return DateTime.local(value.year, value.month + 1, 1).toJSDate();
  }

  if (props.type === DatePickerType.YEAR && typeof value === "number") {
    return DateTime.local(value, 1, 1).toJSDate();
  }

  const date = new Date(value as string | number);
  return isNaN(date.valueOf()) ? new Date() : date;
}

function formatter(dateProp: unknown) {
  let date = dateProp;
  if (date == null) {
    return "";
  }

  if (Array.isArray(date)) {
    date = date.map((d) => {
      if (d == null) {
        return null;
      }

      return toFormatterDate(d);
    }) as [Date | null, Date | null];
  } else {
    date = toFormatterDate(date);
  }

  const normalizedDate = date as Date | [Date | null, Date | null];

  if (props.type === DatePickerType.WEEK) {
    /**
     * Week is a tuple, if not parsed by us with the start and end of the selected week
     * We are only interested in the start
     */
    if (Array.isArray(normalizedDate)) {
      return `${normalizedDate[0] == null ? undefined : DateTime.fromJSDate(normalizedDate[0]).toFormat(t(`components.shared.DatePickerComponent.week`))} - ${normalizedDate[1] == null ? undefined : DateTime.fromJSDate(normalizedDate[1]).toFormat(t(`components.shared.DatePickerComponent.week`))}`;
    } else {
      return DateTime.fromJSDate(normalizedDate).toFormat(
        t(`components.shared.DatePickerComponent.week`),
      );
    }
  }

  /**
   * If it's a range, we return it like this: <date> - <date>
   */
  if (Array.isArray(normalizedDate)) {
    return `${
      normalizedDate[0] == null
        ? ""
        : DateTime.fromJSDate(normalizedDate[0]).toFormat(
            t(`components.shared.DatePickerComponent.${formatString.value}`).toString(),
          )
    } - ${
      normalizedDate[1] == null
        ? ""
        : DateTime.fromJSDate(normalizedDate[1]).toFormat(
            t(`components.shared.DatePickerComponent.${formatString.value}`).toString(),
          )
    }`;
  }

  if (props.type === DatePickerType.YEAR) {
    return DateTime.fromJSDate(normalizedDate)
      .startOf("year")
      .toFormat(t(`components.shared.DatePickerComponent.years`).toString());
  }
  return DateTime.fromJSDate(normalizedDate).toFormat(
    t(`components.shared.DatePickerComponent.${formatString.value}`).toString(),
  );
}

function normalizePickerModelValue(modelValue: any) {
  if (
    modelValue == null ||
    (isRangePicker() &&
      Array.isArray(modelValue) &&
      ((!props.partialRange && modelValue.length !== 2) ||
        (props.partialRange &&
          (modelValue.length > 2 ||
            modelValue.length <= 0 ||
            modelValue.every((el) => el == null)))))
  ) {
    return undefined;
  }

  if (props.range) {
    const [start, end] = (modelValue as [DateTime | undefined, DateTime | undefined]).map((el) =>
      el?.setZone(Intl.DateTimeFormat().resolvedOptions().timeZone, {
        keepLocalTime: true,
      }),
    );

    switch (props.type) {
      default:
      case DatePickerType.SECOND:
      case DatePickerType.MINUTE:
      case DatePickerType.HOUR:
      case DatePickerType.DAY:
        return [start?.toJSDate(), end?.toJSDate()];
      case DatePickerType.WEEK:
        return [getWeekPickerModelValue(start), getWeekPickerModelValue(end)];
      case DatePickerType.MONTH:
        return [
          start == null
            ? undefined
            : {
                month: start.month - 1,
                year: start.year,
              },
          end == null
            ? undefined
            : {
                month: end.month - 1,
                year: end.year,
              },
        ];
      case DatePickerType.YEAR:
        return [start?.year, end?.year];
    }
  }

  const value = (modelValue as DateTime).setZone(Intl.DateTimeFormat().resolvedOptions().timeZone, {
    keepLocalTime: true,
  });
  switch (props.type) {
    default:
    case DatePickerType.SECOND:
    case DatePickerType.MINUTE:
    case DatePickerType.HOUR:
    case DatePickerType.DAY:
      return value.toJSDate();
    case DatePickerType.WEEK:
      return getWeekPickerModelValue(value);
    case DatePickerType.MONTH:
      return {
        month: value.month - 1,
        year: value.year,
      };
    case DatePickerType.YEAR:
      return value.year;
  }
}

const displayedModelValue = ref<any>(normalizePickerModelValue(props.modelValue));

function isRangePicker() {
  return props.range === true || Array.isArray(props.modelValue);
}

function isSaveDisabled(value: unknown) {
  if (value == null) {
    return true;
  }

  if (!isRangePicker()) {
    return Array.isArray(value) && (value.length === 0 || value.every((entry) => entry == null));
  }

  if (!Array.isArray(value)) {
    return true;
  }

  if (props.partialRange) {
    return value.length === 0 || value.length > 2 || value.every((entry) => entry == null);
  }

  return value.length !== 2 || value.some((entry) => entry == null);
}

function sameDate(a: Date | undefined, b: Date | undefined) {
  if (a == null || b == null) {
    return a == null && b == null;
  }
  return a.getTime() === b.getTime();
}

function sameValue(a: any, b: any): boolean {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return a == null && b == null;
  }
  if (a instanceof Date && b instanceof Date) {
    return sameDate(a, b);
  }
  if (Array.isArray(a) && Array.isArray(b) && a.length === b.length) {
    return a.every((entry, index) => sameValue(entry, b[index]));
  }
  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) {
      return false;
    }
    return keysA.every((key) => sameValue(a[key], b[key]));
  }
  return false;
}

function setDisplayedModelValue(value: any) {
  if (!sameValue(displayedModelValue.value, value)) {
    displayedModelValue.value = value;
  }
}

function getWeekPickerModelValue(value: DateTime | undefined) {
  if (value == null) {
    return undefined;
  }

  const start = value.startOf("week");
  return Array.from({ length: 7 }, (_, index) => start.plus({ days: index }).toJSDate());
}

function getFirstPickerDateTime(value: any): DateTime | undefined {
  if (value == null) {
    return undefined;
  }

  if (DateTime.isDateTime(value)) {
    return value;
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value);
  }

  if (Array.isArray(value)) {
    return getFirstPickerDateTime(value[0]);
  }

  return undefined;
}

function normalizeInternalModelValue(value: any) {
  if (props.type === DatePickerType.WEEK) {
    if (props.range) {
      if (!Array.isArray(value)) {
        return value;
      }

      return value.map((entry) => getWeekPickerModelValue(getFirstPickerDateTime(entry)));
    }

    return getWeekPickerModelValue(getFirstPickerDateTime(value));
  }

  if (props.type === DatePickerType.MONTH) {
    const getMonthPickerValue = (entry: any) => {
      const date = getFirstPickerDateTime(entry);
      return date == null
        ? undefined
        : {
            month: date.month - 1,
            year: date.year,
          };
    };

    return props.range && Array.isArray(value)
      ? value.map(getMonthPickerValue)
      : getMonthPickerValue(value);
  }

  if (props.type === DatePickerType.YEAR) {
    const getYearPickerValue = (entry: any) => getFirstPickerDateTime(entry)?.year;
    return props.range && Array.isArray(value)
      ? value.map(getYearPickerValue)
      : getYearPickerValue(value);
  }

  return value;
}

watch(
  () => props.modelValue,
  (value) => {
    setDisplayedModelValue(normalizePickerModelValue(value));
  },
  { immediate: true },
);

function formatEmitValue(date: DateTime | undefined, end = false) {
  if (date == null) {
    return undefined;
  }
  switch (props.type) {
    case DatePickerType.SECOND:
      return date
        .setZone(timezone.value, { keepLocalTime: true })
        [end ? "endOf" : "startOf"]("second");
    case DatePickerType.MINUTE:
      return date
        .setZone(timezone.value, { keepLocalTime: true })
        [end ? "endOf" : "startOf"]("minute");
    case DatePickerType.HOUR:
      return date
        .setZone(timezone.value, { keepLocalTime: true })
        [end ? "endOf" : "startOf"]("hour");
    case DatePickerType.DAY:
      return date
        .setZone(timezone.value, { keepLocalTime: true })
        [end ? "endOf" : "startOf"]("day");
    case DatePickerType.MONTH:
      return date
        .setZone(timezone.value, { keepLocalTime: true })
        [end ? "endOf" : "startOf"]("month");
    case DatePickerType.YEAR:
      return date
        .setZone(timezone.value, { keepLocalTime: true })
        [end ? "endOf" : "startOf"]("year");
    case DatePickerType.WEEK:
      return date
        .setZone(timezone.value, { keepLocalTime: true })
        [end ? "endOf" : "startOf"]("week");
    default:
      return date.setZone(timezone.value, { keepLocalTime: true });
  }
}

function handleInternalModelChange(value: any) {
  setDisplayedModelValue(normalizeInternalModelValue(value));
}

function setDayBoundary(value: InternalModelValue, boundary: "start" | "end") {
  const setBoundary = (date: Date) =>
    DateTime.fromJSDate(date)[boundary === "start" ? "startOf" : "endOf"]("day").toJSDate();
  const nextValue =
    value == null
      ? setBoundary(new Date())
      : Array.isArray(value)
        ? value.map((date, index) => {
            const boundaryIndex = boundary === "start" ? 0 : value.length - 1;
            return index === boundaryIndex ? setBoundary(date) : date;
          })
        : setBoundary(value);

  setDisplayedModelValue(nextValue);
  datePicker.value?.updateInternalModelValue(nextValue);
}

function handleCommitModelValue(value: any) {
  setDisplayedModelValue(value);

  if (
    value == null ||
    (isRangePicker() &&
      Array.isArray(value) &&
      ((!props.partialRange && value.length !== 2) ||
        (props.partialRange && (value.length > 2 || value.length <= 0))))
  ) {
    emit("update:modelValue", undefined);
    return;
  }

  if (props.range) {
    switch (props.type) {
      default:
      case DatePickerType.SECOND:
      case DatePickerType.MINUTE:
      case DatePickerType.HOUR:
      case DatePickerType.DAY: {
        const [start, end] = (value as [Date | null, Date | null]).map((el) =>
          el == null ? undefined : DateTime.fromJSDate(el),
        );

        emit("update:modelValue", [formatEmitValue(start), formatEmitValue(end, true)]);
        return;
      }
      case DatePickerType.WEEK: {
        const [startArray, endArray] = value as [Date[] | undefined, Date[] | undefined];
        const start = getFirstPickerDateTime(startArray);
        const end = getFirstPickerDateTime(endArray);
        emit("update:modelValue", [formatEmitValue(start), formatEmitValue(end, true)]);
        return;
      }
      case DatePickerType.MONTH: {
        const [start, end] = (
          value as [
            (
              | {
                  month: number;
                  year: number;
                }
              | undefined
            ),
            (
              | {
                  month: number;
                  year: number;
                }
              | undefined
            ),
          ]
        ).map((el) =>
          el == null
            ? undefined
            : DateTime.now()
                .setZone(timezone.value)
                .set({
                  month: el.month + 1,
                  year: el.year,
                }),
        );
        emit("update:modelValue", [formatEmitValue(start), formatEmitValue(end, true)]);
        return;
      }
      case DatePickerType.YEAR: {
        const [start, end] = (value as [number | undefined, number | undefined]).map((el) =>
          el == null
            ? undefined
            : DateTime.now().setZone(timezone.value).set({
                year: el,
              }),
        );
        emit("update:modelValue", [formatEmitValue(start), formatEmitValue(end, true)]);
        return;
      }
    }
  }

  switch (props.type) {
    default:
    case DatePickerType.SECOND:
    case DatePickerType.MINUTE:
    case DatePickerType.HOUR:
    case DatePickerType.DAY: {
      const date = DateTime.fromJSDate(value as Date);
      emit("update:modelValue", formatEmitValue(date));
      return;
    }
    case DatePickerType.WEEK: {
      const start = getFirstPickerDateTime(value);
      emit("update:modelValue", formatEmitValue(start));
      return;
    }
    case DatePickerType.MONTH: {
      const valueWithType = value as {
        month: number;
        year: number;
      };
      const date = DateTime.now()
        .setZone(timezone.value)
        .set({
          month: valueWithType.month + 1,
          year: valueWithType.year,
        });
      emit("update:modelValue", formatEmitValue(date));
      return;
    }
    case DatePickerType.YEAR: {
      const date = DateTime.now()
        .setZone(timezone.value)
        .set({
          year: value as number,
        });
      emit("update:modelValue", formatEmitValue(date));
      return;
    }
  }
}

async function syncDisplayedModelValue() {
  await nextTick();
  setDisplayedModelValue(normalizePickerModelValue(props.modelValue));
}

function resetDateToToday() {
  const now = DateTime.now();

  if (isRangePicker()) {
    datePicker.value?.setMonthYear({
      month: now.month - 1,
      year: now.year,
    });
  }

  if (props.range) {
    const value = [formatEmitValue(now), formatEmitValue(now)];
    setDisplayedModelValue(normalizePickerModelValue(value));
    emit("update:modelValue", value);
  } else {
    const value = formatEmitValue(now);
    setDisplayedModelValue(normalizePickerModelValue(value));
    emit("update:modelValue", value);
  }
}

const localeObject = computedAsync(() => {
  const localeTyped = locale.value.replace("-", "_") as keyof typeof languageNames;
  const importObject: Record<keyof typeof languageNames, () => Promise<Locale>> = {
    de_CH: async () => (await import("date-fns/locale/de")).de,
    de: async () => (await import("date-fns/locale/de")).de,
    fr: async () => (await import("date-fns/locale/fr")).fr,
    es: async () => (await import("date-fns/locale/es")).es,
    it: async () => (await import("date-fns/locale/it")).it,
    pt: async () => (await import("date-fns/locale/pt")).pt,
    en: async () => (await import("date-fns/locale/en-US")).enUS,
    en_GB: async () => (await import("date-fns/locale/en-GB")).enGB,
    hu: async () => (await import("date-fns/locale/hu")).hu,
    pl: async () => (await import("date-fns/locale/pl")).pl,
    uk_UA: async () => (await import("date-fns/locale/uk")).uk,
  };
  return importObject[localeTyped]?.() ?? importObject.en();
});

defineOptions({
  inheritAttrs: false,
});

// LISA3
function dateToTemporalPlainDate(value: Date | undefined): LISATemporalPlainDate | undefined {
  if (value == null) {
    return undefined;
  }

  return {
    year: value.getFullYear(),
    month: value.getMonth() + 1,
    day: value.getDate(),
  };
}

function temporalPlainDateToDate(value: LISATemporalPlainDate): Date {
  return new Date(value.year, value.month - 1, value.day);
}

function toLISADatePickerDateDisabledFunction(
  dateDisabledFn: ((date: Date) => boolean) | undefined,
): LISADatePickerDateDisabledFunction | undefined {
  if (dateDisabledFn == null) {
    return undefined;
  }

  return (date) => dateDisabledFn(temporalPlainDateToDate(date));
}

function dateTimeToTemporalPlainDate(
  value: DateTime | undefined,
): LISATemporalPlainDate | undefined {
  if (value == null) {
    return undefined;
  }

  return {
    year: value.year,
    month: value.month,
    day: value.day,
  };
}
const lisa3ModelValue = computed<LISADatePickerValue | undefined>(() => {
  if (props.modelValue == null) {
    return undefined;
  }

  if (Array.isArray(props.modelValue)) {
    const [start, end] = props.modelValue;

    if (start == null || end == null) {
      return undefined;
    }

    return [dateTimeToTemporalPlainDate(start)!, dateTimeToTemporalPlainDate(end)!];
  }

  return dateTimeToTemporalPlainDate(props.modelValue);
});
function temporalDateToDateTime(value: LISADatePickerDate): DateTime {
  if ("epochMilliseconds" in value) {
    return DateTime.fromMillis(value.epochMilliseconds).setZone(value.timeZoneId);
  }

  return DateTime.fromObject(
    {
      year: value.year,
      month: value.month,
      day: value.day,
    },
    {
      zone: timezone.value,
    },
  );
}
function isLISADatePickerRangeValue(value: LISADatePickerValue): value is LISADatePickerRangeValue {
  return Array.isArray(value);
}
function handleLISA3ModelValue(value: LISADatePickerValue | undefined) {
  if (value == null) {
    emit("update:modelValue", undefined);
    return;
  }

  if (isLISADatePickerRangeValue(value)) {
    emit("update:modelValue", [
      temporalDateToDateTime(value[0]),
      value[1] ? temporalDateToDateTime(value[1]) : undefined,
    ]);

    return;
  }

  emit("update:modelValue", temporalDateToDateTime(value));
}

function toLISADatePickerGranularity(type: DatePickerType | undefined): LISADatePickerGranularity {
  switch (type) {
    case DatePickerType.WEEK:
      return "week";

    case DatePickerType.YEAR:
      return "year";

    case DatePickerType.HOUR:
      return "hour";

    case DatePickerType.SECOND:
      return "second";

    case DatePickerType.MINUTE:
    case DatePickerType.DAY:
    case DatePickerType.MONTH:
    default:
      return "minute";
  }
}
</script>

<style lang="scss" scoped>
.date-picker-input-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.75rem;
  width: 100%;
}

.date-picker-hours-separator {
  align-self: center;
  color: var(--p-inputtext-color);
  font-size: 1rem;
}

.date-picker-hours-input {
  width: 10rem;
}

@media (max-width: 640px) {
  .date-picker-input-layout {
    grid-template-columns: 1fr;
  }

  .date-picker-hours-input {
    width: 100%;
  }
}
</style>

<style lang="scss">
.CustomDatePickerClassInput {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-top-right-radius: var(--p-border-radius-md) !important;
  border-bottom-right-radius: var(--p-border-radius-md) !important;
  padding-top: calc(var(--p-form-field-padding-y) / 2 + 1.25px) !important;
  padding-bottom: calc(var(--p-form-field-padding-y) / 2 + 1.25px) !important;
}
.CustomDatePickerClassMenu {
  .dp--tp-wrap {
    max-width: 100% !important;
  }
  .dp__input::placeholder {
    opacity: 1;
  }

  .dp__cell_highlight {
    border-color: var(--dp-highlight-color) !important;
    color: var(--p-primary-contrast-color) !important;
  }
  .dp__range_between_week,
  .dp__range_end {
    border-color: var(--p-button-primary-background) !important;
    background-color: var(--p-button-primary-background) !important;
    color: var(--p-primary-contrast-color) !important;
  }
}
.dp__theme_light {
  --dp-primary-color: var(--p-button-primary-background) !important;
  --dp-text-color: var(--p-text-color) !important;
  --dp-highlight-color: var(--p-blue-100) !important;
  --dp-secondary-color: var(--p-gray-300) !important;
}
:root {
  --dp-cell-border-radius: var(--p-border-radius-md) !important;
  --dp-border-radius: var(--p-border-radius-md) !important;
}
</style>
